import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {Container, media, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import {AnimatePresence} from 'motion/react';
import * as motion from 'motion/react-m';

import {Button} from 'UI/components/action/Button';
import {Form} from 'UI/components/action/Form';
import {TextAreaInput} from 'UI/components/action/inputs/TextAreaInput';
import {TextInput} from 'UI/components/action/inputs/TextInput';
import {UploadInput} from 'UI/components/action/inputs/UploadInput';
import {H4, P} from 'UI/components/layout/Text';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';

import {FadeInAnimation} from 'UI/animations/shared';
import {HintAnimation} from 'UI/animations/textInput';

import {useApplicationBlock} from './useApplicationBlock';
import {merge, required, validEmail, validFileSize, validPhone} from 'UI/utils/validations';

/**
 * The `ComponentProps` interface defines the properties for the `ApplicationBlock` component.
 * This interface specifies all the required and optional properties needed to configure a jobs block display.
 * It includes configuration for auto-filling job articles, manually selected job articles, block identification, and introductory content.
 */
interface ComponentProps {
    id: string;
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing.
     *
     * @default 'ApplicationBlock'
     */
    testId?: string;
}

// eslint-disable-next-line max-lines-per-function
const ApplicationBlock = observer(({id, testId = 'ApplicationBlock'}: ComponentProps) => {
    const {colors, error, handleSubmit, isLoading, isSend} = useApplicationBlock(id);

    return (
        <BlockWrapper data-cy={testId}>
            <ViewportTrigger baseComponent={Container}>
                <FormWrapper variants={FadeInAnimation}>
                    <StyledForm id="application" onSubmit={handleSubmit}>
                        <StyledHead as="h3">Bewerbung senden</StyledHead>
                        <TextInput label="Vorname" name="firstName" validation={required} isRequired />
                        <TextInput label="Nachname" name="name" validation={required} isRequired />
                        <TextInput
                            label="E-Mail"
                            name="email"
                            type="email"
                            validation={merge([required, validEmail])}
                            isRequired
                        />
                        <TextInput label="Telefon" name="phone" type="tel" validation={validPhone} />
                        <StyledTextInput
                            label="Nachricht"
                            name="message"
                            validation={required}
                            isRequired
                        />
                        <StyledUploadInput
                            accept={[
                                'application/msword',
                                'application/pdf',
                                'application/vnd.ms-excel',
                                'application/vnd.ms-powerpoint',
                                'application/vnd.oasis.opendocument.text',
                                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                                'image/*'
                            ]}
                            validation={validFileSize}
                        />
                        <AnimatePresence initial={false} mode="sync">
                            {(isSend || error) && (
                                <ExtraHint animate="enter" exit="exit" initial="initial" variants={HintAnimation}>
                                    {(isSend && !error) && (
                                        <P>
                                            <Color $color={colors.tertiary} aria-live="polite">
                                                Vielen Dank für Ihre Bewerbung, wir melden uns baldmöglichst bei
                                                Ihnen.
                                            </Color>
                                        </P>
                                    )}
                                    {error && (
                                        <P>
                                            <Color $color={colors.errorColor} aria-live="polite">
                                                Beim Senden der Bewerbung ist ein Fehler aufgetreten. Bitte
                                                versuchen Sie es erneut.
                                            </Color>
                                        </P>
                                    )}
                                </ExtraHint>
                            )}
                        </AnimatePresence>
                        <StyledButtonWrapper variants={FadeInAnimation}>
                            <Button
                                as="button"
                                isLoading={isLoading}
                                loadingText="Wird versendet"
                                type="submit"
                                variant="primary"
                            >
                                Jetzt Bewerben.
                            </Button>
                        </StyledButtonWrapper>
                    </StyledForm>
                </FormWrapper>
            </ViewportTrigger>
        </BlockWrapper>
    );
});

ApplicationBlock.displayName = 'ApplicationBlock';

export {ApplicationBlock};

const BlockWrapper = styled.div``;

const FormWrapper = styled(motion.div)`
    background-color: ${({theme}) => theme.colors.backgroundLight};
    border-radius: 16px;
    column-gap: ${spacing(7)};
    display: grid;
    grid-template-columns: 1fr;
    max-width: 87.4rem;
    padding: ${spacing(7)} ${spacing(6)};
    row-gap: ${spacing(7)};
    transition: opacity 0.3s ease-in-out;
    width: 100%;

    ${media('sm')} {
        grid-template-columns: 1fr 1fr;
        padding: ${spacing(14)} ${spacing(13)};
    }
`;

const StyledForm = styled(Form)`
    display: contents;
`;

const StyledHead = styled(H4)`
    grid-column: 1 / -1;
`;

const StyledTextInput = styled(TextAreaInput)`
    grid-column: 1 / -1;
`;

const StyledButtonWrapper = styled(motion.div)`
    grid-column: 1 / -1;
`;

const StyledUploadInput = styled(UploadInput)`
    grid-column: 1 / -1;
`;

const ExtraHint = styled(motion.div)`
    grid-column: 1 / -1;
    height: 0;
    overflow: hidden;

    & p {
        font-size: 1.2rem;

        ${media('md')} {
            font-size: 1.4rem;
        }
    }
`;